import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import coursesApi from 'Api/axios/courses-api';
import userCourseHistoryApi from 'Api/axios/user-course-histories-api';
import useTranslation from 'Hooks/useTranslation';
import { downloadFile } from 'Utils/file-utils';
import type {
  HistoryCourseEmployeesFilterI,
  HistoryCourseEmployeeI,
  HistoryCourseEmployeesTotalsI,
  PaginationMetaI,
  CourseI,
} from 'types';

const defaultTotals: HistoryCourseEmployeesTotalsI = {
  totalEmployees: 0,
  pendingCourses: 0,
  approvedCourses: 0,
  investedTime: 0,
  totalTime: 0,
};

export const useHistoryCourseDetailPage = (courseId: string) => {
  const history = useHistory();
  const { t2 } = useTranslation('history_course_detail');
  const [currentFilters, setCurrentFilters] = useState<HistoryCourseEmployeesFilterI>({});
  const [data, setData] = useState<HistoryCourseEmployeeI[]>([]);
  const [pagination, setPagination] = useState<PaginationMetaI | null>(null);
  const [totals, setTotals] = useState<HistoryCourseEmployeesTotalsI>(defaultTotals);
  const [course, setCourse] = useState<CourseI | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCourseEmployees = async (filters: HistoryCourseEmployeesFilterI = {}) => {
    setLoading(true);
    setError(null);

    const response = await userCourseHistoryApi.getCourseResource(courseId, filters);

    setLoading(false);

    if (response.data) {
      setData(response.data.rows ?? []);
      setTotals(response.data.totals ?? defaultTotals);
      setPagination(response.pagination ?? null);
    } else {
      setError(response.error ?? 'No hay datos disponibles');
    }
  };

  const handleSearch = (filters: HistoryCourseEmployeesFilterI) => {
    setCurrentFilters(filters);
    fetchCourseEmployees({ ...filters, page: 1 });
  };

  const handlePageChange = (page: number) => {
    const newFilters = { ...currentFilters, page };
    setCurrentFilters(newFilters);
    fetchCourseEmployees(newFilters);
  };

  const handleRowsPerPageChange = (limit: number) => {
    const newFilters = { ...currentFilters, limit, page: 1 };
    setCurrentFilters(newFilters);
    fetchCourseEmployees(newFilters);
  };

  const handleExport = async () => {
    const response = await userCourseHistoryApi.exportCourseResource(courseId, currentFilters);

    if (response.data) {
      downloadFile(response.data, `historial_curso_${course?.name ?? 'curso'}.xlsx`);
    }
  };

  const handleDownloadCertificate = async (historyId: string, certificationId: string) => {
    try {
      const response = await userCourseHistoryApi.downloadCertificate(historyId, certificationId);
      if (response.data) {
        downloadFile(response.data, `certificado_curso.pdf`);
        toast.success(t2('certificate_downloaded'));
      }
    } catch (err) {
      console.error('Error downloading certificate:', err);
    }
  };

  const handleRowClick = (row: { userId: string }) => {
    history.push(`/reporte/historial/empleados/${row.userId}`);
  };

  const fetchCourseInfo = async () => {
    const response = await coursesApi.readUnique(courseId);
    if (response.data) {
      setCourse(response.data);
    }
  };

  useEffect(() => {
    if (courseId) {
      fetchCourseInfo();
      fetchCourseEmployees({ page: 1, limit: 15 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  return {
    t2,
    data,
    pagination,
    totals,
    loading,
    error,
    course,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleExport,
    handleDownloadCertificate,
    handleRowClick,
  };
};
